/**
 * WebSocket API Service for AWS Training Assistant
 * Handles real-time streaming communication using WebSocket connections
 * 
 * This service provides TRUE REAL-TIME STREAMING via WebSockets.
 * For buffered HTTP streaming, use streamingApi.ts instead.
 * 
 * Endpoint: wss://[api-id].execute-api.[region].amazonaws.com/prod
 * - Streams response chunks as they are generated in real-time
 * - No buffering - chunks arrive immediately
 * - Bidirectional communication
 * - Suitable for interactive, real-time responses
 */

import { fetchAuthSession } from 'aws-amplify/auth';

// WebSocket API endpoint from environment or default
const WS_API_ENDPOINT = process.env.REACT_APP_WS_API_ENDPOINT || 
  'wss://gc3gof5x6e.execute-api.us-east-1.amazonaws.com/prod';

/**
 * Represents a chunk of data received from the WebSocket
 */
export interface WebSocketChunk {
  text?: string;          // Incremental text content
  json_chunk?: string;    // Incremental JSON content
  json_complete?: boolean; // Signal that JSON is complete
  type?: 'text' | 'json'; // Type of content
  error?: string;         // Error message if applicable
  tool?: string;          // Tool name if tool was executed
  status?: string;        // Tool execution status
  done?: boolean;         // Signal that streaming is complete
}

/**
 * Callback functions for handling WebSocket events
 */
export interface WebSocketCallbacks {
  onChunk: (text: string) => void;           // Called when a text chunk is received
  onJsonChunk?: (jsonChunk: string) => void; // Called when a JSON chunk is received
  onJsonComplete?: (json: any) => void;      // Called when complete JSON is received
  onComplete: () => void;                    // Called when the stream completes successfully
  onError: (error: string) => void;          // Called when an error occurs
  onOpen?: () => void;                       // Called when WebSocket connection opens
  onClose?: () => void;                      // Called when WebSocket connection closes
}

/**
 * Configuration options for WebSocket requests
 */
export interface WebSocketOptions {
  timeoutMs?: number; // Timeout in milliseconds (default: 300000 = 5 minutes)
}

/**
 * WebSocket connection manager
 */
class WebSocketManager {
  private ws: WebSocket | null = null;
  private callbacks: WebSocketCallbacks | null = null;
  private jsonBuffer: string = '';
  private isJsonResponse: boolean = false;
  private timeoutId: NodeJS.Timeout | null = null;

  /**
   * Connect to WebSocket API with authentication
   */
  async connect(callbacks: WebSocketCallbacks, options?: WebSocketOptions): Promise<void> {
    const timeoutMs = options?.timeoutMs ?? 300000; // Default 5 minutes

    try {
      // Get authentication token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('Authentication required. Please sign in.');
      }

      // Create WebSocket connection with auth token in query string
      // Note: WebSocket API doesn't support Authorization header, so we pass token as query param
      const wsUrl = `${WS_API_ENDPOINT}?token=${encodeURIComponent(token)}`;
      
      this.ws = new WebSocket(wsUrl);
      this.callbacks = callbacks;
      this.jsonBuffer = '';
      this.isJsonResponse = false;

      // Set up event handlers
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        if (this.callbacks?.onOpen) {
          this.callbacks.onOpen();
        }
        this.resetTimeout(timeoutMs);
      };

      this.ws.onmessage = (event) => {
        this.resetTimeout(timeoutMs);
        this.handleMessage(event.data);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.clearTimeout();
        if (this.callbacks?.onError) {
          this.callbacks.onError('WebSocket connection error');
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket closed');
        this.clearTimeout();
        if (this.callbacks?.onClose) {
          this.callbacks.onClose();
        }
      };

      // Wait for connection to open
      await new Promise<void>((resolve, reject) => {
        if (!this.ws) {
          reject(new Error('WebSocket not initialized'));
          return;
        }

        const openHandler = () => {
          resolve();
        };

        const errorHandler = () => {
          reject(new Error('Failed to connect to WebSocket'));
        };

        this.ws.addEventListener('open', openHandler, { once: true });
        this.ws.addEventListener('error', errorHandler, { once: true });
      });
    } catch (error) {
      this.clearTimeout();
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Failed to connect to WebSocket');
      }
    }
  }

  /**
   * Send a message through the WebSocket
   */
  sendMessage(prompt: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    const message = {
      action: 'sendMessage',
      prompt: prompt
    };

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: string): void {
    try {
      const chunk: WebSocketChunk = JSON.parse(data);

      if (chunk.error) {
        this.clearTimeout();
        if (this.callbacks?.onError) {
          this.callbacks.onError(chunk.error);
        }
        return;
      }

      // Handle completion
      if (chunk.done) {
        this.clearTimeout();
        if (this.callbacks?.onComplete) {
          this.callbacks.onComplete();
        }
        return;
      }

      // Handle JSON chunks
      if (chunk.type === 'json' && chunk.json_chunk) {
        this.isJsonResponse = true;
        this.jsonBuffer += chunk.json_chunk;
        
        if (this.callbacks?.onJsonChunk) {
          this.callbacks.onJsonChunk(chunk.json_chunk);
        }
      }

      // Handle JSON completion
      if (chunk.type === 'json' && chunk.json_complete) {
        try {
          const parsedJson = JSON.parse(this.jsonBuffer);
          
          if (this.callbacks?.onJsonComplete) {
            this.callbacks.onJsonComplete(parsedJson);
          }
          
          this.jsonBuffer = '';
        } catch (parseError) {
          console.error('Failed to parse complete JSON:', parseError);
          if (this.callbacks?.onError) {
            this.callbacks.onError('Failed to parse JSON response');
          }
        }
      }

      // Handle text chunks (non-JSON)
      if (chunk.text && !this.isJsonResponse) {
        if (this.callbacks?.onChunk) {
          this.callbacks.onChunk(chunk.text);
        }
      }

      // Handle tool execution notifications
      if (chunk.tool && chunk.status) {
        console.log(`Tool executed: ${chunk.tool} - ${chunk.status}`);
      }
    } catch (e) {
      console.error('Failed to parse WebSocket message:', e);
    }
  }

  /**
   * Reset the timeout timer
   */
  private resetTimeout(timeoutMs: number): void {
    this.clearTimeout();
    
    this.timeoutId = setTimeout(() => {
      if (this.callbacks?.onError) {
        this.callbacks.onError(`WebSocket timeout: No data received for ${timeoutMs / 1000} seconds`);
      }
      this.disconnect();
    }, timeoutMs);
  }

  /**
   * Clear the timeout timer
   */
  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.clearTimeout();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.callbacks = null;
    this.jsonBuffer = '';
    this.isJsonResponse = false;
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

/**
 * Stream a chat response using WebSocket
 * 
 * @param prompt - The user's question/prompt
 * @param callbacks - Callback functions for handling stream events
 * @param options - Optional configuration (e.g., timeout)
 * @returns Promise that resolves when the stream completes or rejects on error
 * @throws Error with user-friendly message for various failure scenarios
 */
export async function streamChatResponseWS(
  prompt: string,
  callbacks: WebSocketCallbacks,
  options?: WebSocketOptions
): Promise<void> {
  const manager = new WebSocketManager();

  try {
    // Connect to WebSocket
    await manager.connect(callbacks, options);

    // Send message
    manager.sendMessage(prompt);

    // Wait for completion or error
    await new Promise<void>((resolve, reject) => {
      const originalOnComplete = callbacks.onComplete;
      const originalOnError = callbacks.onError;

      callbacks.onComplete = () => {
        originalOnComplete();
        manager.disconnect();
        resolve();
      };

      callbacks.onError = (error: string) => {
        originalOnError(error);
        manager.disconnect();
        reject(new Error(error));
      };
    });
  } catch (error) {
    manager.disconnect();
    throw error;
  }
}

/**
 * Export WebSocketManager for advanced use cases
 */
export { WebSocketManager };
